import { getErrorMessage } from "@/utils/errorHelpers";
import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { authAPI, publicAPI } from "@/services/api";
import { wakeBackend } from "@/services/backendWake";
import { MMJ_LEGAL_VERSION } from "@/pages/LegalPage";
import { COUNTRIES, getCountryByCode, prefixPhoneWithDialCode } from "@/utils/countries";
import PublicContactFaqSection from "@/components/app/PublicContactFaqSection";
import { toast } from "sonner";
import { Home } from "lucide-react";

export default function RegisterPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [parrainStatus, setParrainStatus] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirm_password: "",
    full_name: "",
    telephone: "",
    country_code: "CD",
    role: searchParams.get("parrain") ? "partner" : "buyer",
    parrain_id: searchParams.get("parrain") || "",
    legal_accepted: false,
    legal_version: MMJ_LEGAL_VERSION,
  });

  useEffect(() => {
    wakeBackend({ force: true, reason: "register" });
  }, []);

  useEffect(() => {
    // Mettre à jour le parrain_id si présent dans l'URL
    const parrainFromUrl = searchParams.get("parrain");
    if (parrainFromUrl) {
      setFormData(prev => ({ ...prev, parrain_id: parrainFromUrl, role: "partner" }));
    }
  }, [searchParams]);

  useEffect(() => {
    const parrainId = formData.parrain_id.trim();
    if (formData.role !== "partner" || !parrainId) {
      setParrainStatus(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await publicAPI.searchUser(parrainId);
        setParrainStatus(res.data);
      } catch (error) {
        if (!error?.response || error?.code === "ECONNABORTED") {
          setParrainStatus({ exists: null, id: parrainId, error: "server" });
          return;
        }
        setParrainStatus({ exists: false, id: parrainId });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [formData.parrain_id, formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = { ...formData };
      if (data.password !== data.confirm_password) {
        toast.error("Les deux mots de passe ne correspondent pas.");
        setLoading(false);
        return;
      }
      if (!data.legal_accepted) {
        toast.error("Vous devez accepter les conditions d'utilisation et la politique de confidentialite.");
        setLoading(false);
        return;
      }
      delete data.confirm_password;
      delete data.country_code;
      if (!data.parrain_id) delete data.parrain_id;
      if (data.role === "partner" && data.parrain_id && parrainStatus?.exists === null) {
        toast.error("Serveur inaccessible. Verifiez la connexion puis reessayez.");
        setLoading(false);
        return;
      }
      if (data.role === "partner" && data.parrain_id && parrainStatus && parrainStatus.exists === false) {
        toast.error("ID parrain introuvable");
        setLoading(false);
        return;
      }

      const response = await authAPI.register(data);
      const { token, user } = response.data;
      onLogin(token, user);
      toast.success("Inscription reussie. Verifiez votre e-mail pour continuer.");
      navigate("/verify-email", { state: { otpSent: true } });
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur d'inscription"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF8] px-4 py-5 sm:px-6 sm:py-8">
      <div className="fixed left-3 top-3 z-10 sm:left-4 sm:top-4">
        <Link to="/">
          <Button variant="ghost" className="h-9 px-3 text-[#2A4B46]">
            <Home size={16} className="mr-2" /> Accueil
          </Button>
        </Link>
      </div>
      <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-md items-start justify-center pt-12 sm:min-h-[calc(100vh-4rem)] sm:items-center sm:pt-0">
        <Card className="w-full border-[#E6E4E0] shadow-lg" data-testid="register-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-bold text-[#2A4B46] font-['Outfit'] sm:text-2xl" data-testid="register-title">
            Inscription
            </CardTitle>
            <CardDescription className="text-sm sm:text-base">Créez votre compte MMJ</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
          {searchParams.get("parrain") && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3">
              <p className="text-xs font-semibold text-green-800 sm:text-sm">
                ✅ Lien de parrainage détecté !
              </p>
              <p className="mt-1 text-[11px] text-green-700 sm:text-xs">
                Vous allez créer un compte partenaire avec le parrain ID: {searchParams.get("parrain").substring(0, 8)}...
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="full_name">Nom complet</Label>
              <Input
                id="full_name"
                type="text"
                data-testid="register-name-input"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="register-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label htmlFor="country_code">Pays</Label>
              <Select
                value={formData.country_code}
                onValueChange={(code) => {
                  const country = getCountryByCode(code);
                  setFormData({
                    ...formData,
                    country_code: country.code,
                    telephone: prefixPhoneWithDialCode(formData.telephone, country.dialCode),
                  });
                }}
              >
                <SelectTrigger className="mt-1 h-10" data-testid="register-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>{country.name} ({country.dialCode})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="telephone">numéro de telephone</Label>
              <Input
                id="telephone"
                type="tel"
                data-testid="register-phone-input"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                required
                className="mt-1 h-10"
                placeholder="+243..."
              />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                data-testid="register-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1 h-10"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
              <Input
                id="confirm_password"
                type="password"
                data-testid="register-confirm-password-input"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                required
                className="mt-1 h-10"
              />
              {formData.confirm_password && formData.password !== formData.confirm_password && (
                <p className="mt-1 text-[11px] text-red-700">Les mots de passe ne correspondent pas.</p>
              )}
            </div>
            <div>
              <Label htmlFor="role">Type de compte</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="mt-1 h-10" data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer">Acheteur</SelectItem>
                  <SelectItem value="partner">Partenaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role === "partner" && (
              <div>
                <Label htmlFor="parrain_id">ID du parrain (optionnel)</Label>
                <Input
                  id="parrain_id"
                  type="text"
                  data-testid="register-parrain-input"
                  value={formData.parrain_id}
                  onChange={(e) => setFormData({ ...formData, parrain_id: e.target.value.toUpperCase() })}
                  className="mt-1 h-10"
                  placeholder="Laissez vide si aucun parrain"
                />
                {parrainStatus?.exists && (
                  <p className="mt-1 text-[11px] text-green-700">Parrain confirme : {parrainStatus.full_name} ({parrainStatus.id})</p>
                )}
                {parrainStatus?.exists === false && formData.parrain_id && (
                  <p className="mt-1 text-[11px] text-red-700">Aucun parrain trouve pour {parrainStatus.id}</p>
                )}
                {parrainStatus?.exists === null && formData.parrain_id && (
                  <p className="mt-1 text-[11px] text-amber-700">Serveur inaccessible pour verifier {parrainStatus.id}. Reessayez dans quelques secondes.</p>
                )}
              </div>
            )}
            <div className="rounded-xl border border-[#E6E4E0] bg-[#F8F5EF] p-2.5">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="legal_accepted"
                  checked={formData.legal_accepted}
                  onCheckedChange={(checked) => setFormData({ ...formData, legal_accepted: checked === true })}
                  className="mt-1 border-[#2A4B46] data-[state=checked]:bg-[#2A4B46]"
                  data-testid="register-legal-checkbox"
                />
                <Label htmlFor="legal_accepted" className="text-sm leading-5 text-[#424846]">
                  J'ai lu et j'accepte les{" "}
                  <Link to="/legal" target="_blank" className="font-semibold text-[#2A4B46] underline">
                    conditions d'utilisation et la politique de confidentialite
                  </Link>{" "}
                  de MMJ.
                </Label>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="register-submit-button"
              disabled={loading || !formData.legal_accepted}
              className="h-11 w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]"
            >
              {loading ? "Inscription..." : "S'inscrire"}
            </Button>
          </form>

          <div className="mt-3 text-center text-sm text-[#5C625F]">
            Déjà un compte ?{" "}
            <Link to="/login" className="text-[#2A4B46] font-semibold hover:underline" data-testid="login-link">
              Se connecter
            </Link>
          </div>
          </CardContent>
        </Card>
      </div>
      <PublicContactFaqSection compact className="mt-8 sm:mt-10" />
    </div>
  );
}
